"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.dot = dot;
/**
 * Converts a nested object into a flattened object with dot notation keys.
 *
 * This function takes an object with potentially nested properties and flattens it
 * so that the keys represent the path to the original nested properties, joined by dots.
 *
 * @param {Record<string, string | object>} object - The object to be flattened.
 * @param {string} [prefix=''] - The prefix for the keys, used for recursion. Defaults to an empty string.
 * @returns {Record<string, string>} A new object with flattened keys in dot notation.
 *
 * @example
 * // Example usage:
 * const nestedObject = {
 *     a: 'value1',
 *     b: {
 *         c: 'value2',
 *         d: {
 *             e: 'value3'
 *         }
 *     }
 * };
 *
 * const result = dot(nestedObject);
 * console.log(result);
 * // Output:
 * // {
 * //     'a': 'value1',
 * //     'b.c': 'value2',
 * //     'b.d.e': 'value3'
 * // }
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function dot(object, prefix = '') {
    const result = {};
    Object.keys(object).forEach((key) => {
        const newPrefix = prefix ? `${prefix}.${key}` : key;
        const value = object[key];
        if (value === null) {
            result[newPrefix] = 'null';
        }
        else if (typeof value === 'boolean') {
            result[newPrefix] = value ? 'true' : 'false';
        }
        else if (typeof value === 'object') {
            Object.assign(result, dot(value, newPrefix));
        }
        else {
            result[newPrefix] = value;
        }
    });
    return result;
}
